// // Web3AuthService.ts
// import { CHAIN_NAMESPACES } from "@web3auth/base";
// import { EthereumPrivateKeyProvider } from "@web3auth/ethereum-provider";
// import { Web3AuthNoModal } from "@web3auth/no-modal";
// import { OpenloginAdapter, WhiteLabelData } from "@web3auth/openlogin-adapter";

// //ui-config
// const uiConfig: WhiteLabelData = {
//   appName: "penomo-protocol",
// }

// // Mainnet
// const chainConfig = {
//   chainNamespace: CHAIN_NAMESPACES.EIP155,
//   chainId: "0x1",
//   rpcTarget: process.env.REACT_APP_MAINNET_RPC_URL || "",
//   displayName: "Ethereum Mainnet",
//   blockExplorer: "https://etherscan.io/",
//   ticker: "ETH",
//   tickerName: "Ethereum",
//   logo: "https://images.toruswallet.io/eth.svg",
// };
// const clientId = process.env.REACT_APP_WEB3AUTH_ID || "";
// const privateKeyProvider = new EthereumPrivateKeyProvider({ config: { chainConfig } });

// const web3auth = new Web3AuthNoModal({
//   clientId,
//   chainConfig,
//   web3AuthNetwork: "sapphire_mainnet",
//   uiConfig
// });

// const openloginAdapter = new OpenloginAdapter({
//   privateKeyProvider: privateKeyProvider,
// });
// web3auth.configureAdapter(openloginAdapter);

// export { web3auth };

import { CHAIN_NAMESPACES, CustomChainConfig, WEB3AUTH_NETWORK } from "@web3auth/base";
import { EthereumPrivateKeyProvider } from "@web3auth/ethereum-provider";
import { Web3AuthNoModal } from "@web3auth/no-modal";
import { AuthAdapter, WhiteLabelData } from "@web3auth/auth-adapter";
import { useNetwork } from "@/provider/NetworkContext";
import { useMemo, useCallback } from "react";
import toast from "react-hot-toast";

// UI Config
const uiConfig: WhiteLabelData = {
  appName: "penomo-protocol",
};
// Chain configurations
const chainConfigs: { [key: string]: CustomChainConfig } = {
  bsc: {
    chainNamespace: CHAIN_NAMESPACES.EIP155,
    chainId: "0x38", // BSC Mainnet
    rpcTarget: process.env.REACT_APP_BSC_MAINNET_RPC_URL || "https://bsc-dataseed1.binance.org/",
    displayName: "BNB Smart Chain",
    blockExplorerUrl: "https://bscscan.com/",
    ticker: "BNB",
    tickerName: "BNB",
  },
  ethereum: {
    chainNamespace: CHAIN_NAMESPACES.EIP155, // EIP-155 is used for Ethereum and Ethereum-like networks
    chainId: "0x1", // Ethereum Mainnet chain ID
    rpcTarget: process.env.REACT_APP_MAINNET_RPC_URL || "", // Default to an Ethereum RPC provider (Infura in this case)
    displayName: "Ethereum Mainnet",
    blockExplorerUrl: "https://etherscan.io/",
    ticker: "ETH", // Ethereum's native currency ticker
    tickerName: "Ether",
  },
};

// Function to create Web3Auth instance
const createWeb3AuthInstance = () => {
  const privateKeyProvider = new EthereumPrivateKeyProvider({ config: { chainConfig: chainConfigs.bsc } });
  const clientId = process.env.REACT_APP_WEB3AUTH_ID || "";
  const web3authInstance = new Web3AuthNoModal({
    clientId,
    chainConfig: chainConfigs.bsc, // Default to bsc
    web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_MAINNET,
    privateKeyProvider,
    uiConfig,
  });

  const authAdapter = new AuthAdapter({
    privateKeyProvider: privateKeyProvider,
    adapterSettings: {
      loginConfig: {
        email_passwordless: {
          verifier: "no_modal_only_email_verifier",
          typeOfLogin: "email_passwordless",
          clientId,
        },
      },
    },
  });
  web3authInstance.configureAdapter(authAdapter);

  return web3authInstance;
};

// Create a singleton instance
const web3authInstance = createWeb3AuthInstance();

// Hook to use Web3Auth
const useWeb3Auth = () => {
  const { selectedNetwork } = useNetwork();

  const switchNetwork = useCallback(async () => {
    if (!web3authInstance.provider) {
      throw new Error("Provider not initialized");
    }

    const chainConfig = chainConfigs[selectedNetwork];
    if (!chainConfig) {
      throw new Error("Invalid network selected");
    }

    try {
      await web3authInstance.addChain(chainConfig);
      await web3authInstance.switchChain({ chainId: chainConfig.chainId });
    } catch (error) {
      toast.error("Error switching chain");
      throw error;
    }
  }, [selectedNetwork]);

  const initializeWeb3Auth = useCallback(async () => {
    if (!web3authInstance.provider) {
      await web3authInstance.init();
    }
    await switchNetwork();
  }, [switchNetwork]);

  return useMemo(
    () => ({
      web3auth: web3authInstance,
      initializeWeb3Auth,
      switchNetwork,
    }),
    [initializeWeb3Auth, switchNetwork]
  );
};

export { useWeb3Auth, web3authInstance };
